import {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, CircularProgress} from '@mui/material'
import {useQuery, gql} from '@apollo/client'
import FilterHeader from '../FilterHeader/FilterHeader'
import RangeSlider from '../RangeSlider'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'
import {useStatesAndHealthConditions} from '../../../store/hooks/breadCrumbs'
import loadingTitles from '../../../appConfig/loadingTitles.config.json'

const detectFilterCategory = (categoryId) => {
  if (!categoryId) return null
  if (categoryId === '6' || categoryId === 'geographic') {
    return {category: 'geographic', maxCap: 500}
  }
  if (categoryId === '7' || categoryId === 'climate') {
    return {category: 'climate', maxCap: 500}
  }
  return null
}

/**
 * RangeFilterControl - FilterHeader + Range Slider Component
 *
 * Intelligent component that auto-detects its mode:
 * 1. Simple mode - Pass min/max/value directly
 * 2. Data-fetching mode - Pass only filterKey, component fetches range from API
 *
 * Usage:
 * // Simple mode (static range)
 * <RangeFilterControl label="Age" min={0} max={100} value={[20, 80]} onChange={...} />
 *
 * // Data-fetching mode (auto-detected from filterKey)
 * <RangeFilterControl
 *   filterKey="estimatedDrivingDistanceHospital"
 *   label="Nearest Hospital"
 * />
 */
const RangeFilterControl = ({
  label = 'Range',
  description,
  min,
  max,
  value,
  onChange,
  onChangeCommitted,
  onRemove,
  showRemoveButton = true,
  useGradient = false,
  step = 1,
  showInputs = true,
  minLabel = '',
  maxLabel = '',
  showCustomLabels = false,
  filterKey = null,
  identifier = null,
  categoryId = null,
  ...rest
}) => {
  const {
    getFilterValue,
    setFilterValue,
    getPendingFilterValue,
    clearPendingFilterValue,
    setFilterRangeOptions,
    getFilterRangeOptions
  } = useFilterManagement()
  const {filters} = useStatesAndHealthConditions()
  const pendingValueAppliedRef = useRef(false)

  const needsDataFetching = filterKey && (min === undefined || max === undefined)
  const apiIdentifier = identifier || filterKey
  const filterSettings = needsDataFetching ? detectFilterCategory(categoryId) : null
  const filterCategory = filterSettings?.category
  const maxCap = filterSettings?.maxCap
  const isDataFetchingMode = needsDataFetching && filterCategory

  const [fetchedOptions, setFetchedOptions] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const prevStateRef = useRef()

  // Check if we already have data in Redux to skip query
  const storedOptionsForSkip = getFilterRangeOptions(filterKey)
  const hasStoredData = storedOptionsForSkip && storedOptionsForSkip.min !== undefined

  const selectedState = filters[0]?.default || []
  const stateId = selectedState.map((stateName) => {
    const stateObj = filters[0]?.values?.find((state) => state.STATE_NAME === stateName)
    return stateObj?.STATE_ID
  })

  const fetchGeographicRangeOptions = gql`
        query EstimatedDrivingDistance${filterKey}($state_id: [Int]) {
          sdohEstimatedDrivingDistance(key: "${apiIdentifier}", stateId: $state_id) {
            POI_MIN_RANGE
            POI_MAX_RANGE
          }
        }
      `

  const fetchClimateRangeOptions = gql`
        query Climate${filterKey}($state_id: [Int]) {
          sdohClimate(key: "${apiIdentifier}", stateId: $state_id) {
            POI_MIN_RANGE
            POI_MAX_RANGE
          }
        }
      `

  const {data: geographicData, loading: geographicLoading} = useQuery(fetchGeographicRangeOptions, {
    skip: !isDataFetchingMode || filterCategory !== 'geographic' || !stateId.length || hasStoredData,
    variables: {state_id: stateId}
  })

  const {data: climateData, loading: climateLoading} = useQuery(fetchClimateRangeOptions, {
    skip: !isDataFetchingMode || filterCategory !== 'climate' || !stateId.length || hasStoredData,
    variables: {state_id: stateId}
  })

  const getCategoryData = () => {
    if (filterCategory === 'geographic' && !geographicLoading && geographicData) {
      return {data: geographicData.sdohEstimatedDrivingDistance, loading: geographicLoading}
    }
    if (filterCategory === 'climate' && !climateLoading && climateData) {
      return {data: climateData.sdohClimate, loading: climateLoading}
    }
    return null
  }

  const applyPendingValue = (pendingValue, optionMax) => {
    if (!pendingValue || pendingValueAppliedRef.current) return false

    const finalValue =
      Array.isArray(pendingValue) && pendingValue[1] === null ? [pendingValue[0], optionMax] : pendingValue

    clearPendingFilterValue(filterKey)
    setFilterValue(filterKey, finalValue)
    pendingValueAppliedRef.current = true
    return true
  }

  useEffect(() => {
    if (!isDataFetchingMode) return

    if (isInitialized) return

    // Check if we already have stored range options in Redux
    const storedOptions = getFilterRangeOptions(filterKey)
    if (storedOptions && !fetchedOptions) {
      setFetchedOptions(storedOptions)
      setIsInitialized(true)
      return
    }

    const categoryData = getCategoryData()
    if (!categoryData) return

    const {data: optionData, loading: isLoading} = categoryData
    if (!optionData || isLoading) return

    const optionMin = optionData.POI_MIN_RANGE
    let optionMax = optionData.POI_MAX_RANGE

    if (maxCap && optionMax > maxCap) {
      optionMax = maxCap
    }

    const newOptions = {
      min: optionMin,
      minLabel: `${optionMin}`,
      max: optionMax,
      maxLabel: `${optionMax}`
    }

    setFetchedOptions(newOptions)
    setFilterRangeOptions(filterKey, newOptions)

    const pendingValue = getPendingFilterValue(filterKey)
    const wasApplied = applyPendingValue(pendingValue, optionMax)

    if (wasApplied) {
      setIsInitialized(true)
      return
    }

    setIsInitialized(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDataFetchingMode,
    filterCategory,
    geographicData,
    climateData,
    geographicLoading,
    climateLoading,
    filterKey,
    maxCap,
    isInitialized
  ])

  useEffect(() => {
    if (!isDataFetchingMode) return

    const currentState = filters[0]?.default?.join(',')
    const prevState = prevStateRef.current

    if (prevState && prevState !== currentState && isInitialized) {
      setFetchedOptions(null)
      setIsInitialized(false)
      setFilterRangeOptions(filterKey, null)
      pendingValueAppliedRef.current = false
    }

    prevStateRef.current = currentState
  }, [filters, isDataFetchingMode, isInitialized, filterKey, setFilterRangeOptions])

  const handleChange = (event, newValue) => {
    if (isDataFetchingMode) {
      setFilterValue(filterKey, newValue)
    }
    if (onChange) {
      onChange(event, newValue)
    }
  }

  const handleChangeCommitted = (event, newValue) => {
    if (isDataFetchingMode) {
      setFilterValue(filterKey, newValue)
    }
    if (onChangeCommitted) {
      onChangeCommitted(event, newValue)
    }
  }

  let actualMin, actualMax, actualValue, actualMinLabel, actualMaxLabel

  if (isDataFetchingMode) {
    if (!fetchedOptions) {
      return (
        <Box width='100%'>
          <FilterHeader
            label={label}
            description={description}
            onRemove={onRemove}
            showRemoveButton={showRemoveButton}
          />
          <Box display='flex' justifyContent='center' alignItems='center' p={2}>
            <CircularProgress size={20} title={loadingTitles.rangeSdohFilter || 'Loading filter options...'} />
          </Box>
        </Box>
      )
    }

    actualMin = fetchedOptions.min
    actualMax = fetchedOptions.max
    actualMinLabel = fetchedOptions.minLabel
    actualMaxLabel = fetchedOptions.maxLabel
    actualValue = getFilterValue(filterKey) || [actualMin, actualMax]
  } else {
    actualMin = min ?? 0
    actualMax = max ?? 100
    actualValue = value || [actualMin, actualMax]
    actualMinLabel = minLabel
    actualMaxLabel = maxLabel
  }

  return (
    <Box width='100%'>
      <FilterHeader
        label={label}
        description={description}
        onRemove={onRemove}
        showRemoveButton={showRemoveButton}
        min={actualMin}
        max={actualMax}
      />
      <RangeSlider
        min={actualMin}
        max={actualMax}
        value={actualValue}
        step={step}
        useGradient={useGradient}
        showInputs={showInputs}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        ariaLabel={label}
        minLabel={actualMinLabel}
        maxLabel={actualMaxLabel}
        showCustomLabels={showCustomLabels}
        {...rest}
      />
    </Box>
  )
}

RangeFilterControl.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.arrayOf(PropTypes.number),
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  onChange: PropTypes.func,
  onChangeCommitted: PropTypes.func,
  onRemove: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  useGradient: PropTypes.bool,
  step: PropTypes.number,
  showInputs: PropTypes.bool,
  showCustomLabels: PropTypes.bool,
  filterKey: PropTypes.string,
  identifier: PropTypes.string,
  categoryId: PropTypes.string
}

export default RangeFilterControl
